<template>
  <div class="top-kpis-section">
    <h3 class="section-title">TOP KPIs</h3>
    <div class="kpis-grid">
      <div
        v-for="metricId in pinnedMetricIds"
        :key="metricId"
        class="kpi-card"
      >
        <div class="kpi-header">
          <span class="kpi-label">{{ getMetric(metricId)?.metric_display_name || '—' }}</span>
          <PinButton
            :isPinned="true"
            :metricId="metricId"
            size="large"
            @toggle="handleTogglePin"
          />
        </div>
        
        <div class="kpi-value-row">
          <span class="kpi-value">{{ getDisplayValue(metricId) }}</span>
          <div 
            v-if="getMetric(metricId) && !getMetric(metricId).percent_change_is_na" 
            class="kpi-trend"
            :class="getTrendClass(metricId)"
          >
            <svg v-if = "getMetric(metricId).direction == 'up'" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M16 6L18.29 8.29L13.41 13.17L9.41 9.17L2 16.59L3.41 18L9.41 12L13.41 16L19.71 9.71L22 12V6H16Z" fill="#25B8C2"/>
            </svg>
            <svg v-else-if = "getMetric(metricId).direction == 'down'" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M16 18L18.29 15.71L13.41 10.83L9.41 14.83L2 7.41L3.41 6L9.41 12L13.41 8L19.71 14.29L22 12V18H16Z" fill="#D10000"/>
            </svg>
            <svg v-else-if = "getMetric(metricId).direction == 'neutral'" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M21.5 12L17.5 8V11H2.5V13H17.5V16L21.5 12Z" fill="#E1A325"/>
            </svg>
            <span class="trend-text">{{ getMetric(metricId).percent_change_display}}</span>
          </div>
        </div>
        
        <span class="kpi-comparison">{{ getMetric(metricId)?.helper_text || 'vs. prev ' + timeRangeLabel }}</span>
      </div>
      
      <div v-if="!pinnedMetricIds.length" class="kpis-empty">
        No KPIs pinned. Configure your dashboard preferences to pin metrics.
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue';
import { useStore } from 'vuex';
import PinButton from './PinButton.vue';

const store = useStore();

const pinnedMetricIds = computed(() => store.getters['myPractice/clinicDashboard/topKpis'] || []);
const timeRangeMap = computed(() => store.state.myPractice.clinicDashboard.timeRangeMap || {});
const timeRangeLabel = computed(() => timeRangeMap.value[store.state.myPractice.clinicDashboard.dashboardFilters.timeRange] || 'Custom Range');

function getMetric(metricId) {
  return store.getters['myPractice/clinicDashboard/getMetricById'](metricId);
}

function getDisplayValue(metricId) {
  const metric = getMetric(metricId);
  if (!metric) return '—';
  return metric.value_display || 'N/A';
}

function getTrendClass(metricId) {
  const metric = getMetric(metricId);
  if (!metric) return '';
  switch (metric.direction) {
    case 'up':
      return 'trend-up';
    case 'down':
      return 'trend-down';
    case 'neutral':
      return 'trend-neutral';
    default:
      return '';
  }
}

function handleTogglePin(metricId) {
  if (!metricId) return;
  store.dispatch('myPractice/clinicDashboard/toggleMetricPin', metricId);
}
</script>

<style scoped>
.top-kpis-section {
  background: #f9fbfd;
  border-radius: 10px;
  padding: 16px 24px;
}

.section-title {
  font-family: 'Inter', sans-serif;
  font-size: 20px;
  font-weight: 700;
  line-height: 28px;
  color: #333;
  margin: 0 0 8px 0;
}

.kpis-grid {
  display: flex;
  gap: 16px;
  border-radius: 10px;
}

.kpi-card {
  flex: 1;
  min-width: 0;
  background: white;
  border-radius: 5px;
  box-shadow: 0 1px 15px 0 rgba(164, 164, 164, 0.25);
  padding: 6px 24px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  justify-content: center;
}

.kpi-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  width: 100%;
}

.kpi-label {
  font-family: 'Inter', sans-serif;
  font-size: 16px;
  font-weight: 500;
  line-height: 24px;
  letter-spacing: 0.15px;
  color: #333;
}


.kpi-value-row {
  display: flex;
  align-items: center;
  gap: 8px;
}

.kpi-value {
  font-family: 'Inter', sans-serif;
  font-size: 28px;
  font-weight: 700;
  line-height: 40px;
  color: #004879;
}

.kpi-trend {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  height: 24px;
}

.trend-icon {
  width: 24px;
  height: 24px;
  object-fit: contain;
}

.trend-text {
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  letter-spacing: 0.1px;
}

.kpi-trend.trend-up .trend-text {
  color: #25B8C2;
}

.kpi-trend.trend-down .trend-text {
  color: #D10000;
}

.kpi-trend.trend-neutral .trend-text {
  color: #E1A325;
}

.kpi-comparison {
  font-family: 'Inter', sans-serif;
  font-size: 12px;
  font-weight: 400;
  line-height: 16px;
  letter-spacing: 0.4px;
  color: #666;
}

.kpis-empty {
  padding: 24px;
  color: #9CA3AF;
  font-size: 14px;
  text-align: center;
  width: 100%;
}
</style>
